import { useState, useEffect } from "react";
import axios from "axios";
import { GoArrowUpRight, GoPeople, GoShield, GoTriangleUp } from "react-icons/go";
import { FaSackDollar, FaPeopleLine, FaArrowsDownToPeople, FaHandHoldingDollar } from "react-icons/fa6";
import { BsPersonSquare } from "react-icons/bs";
import { HiOutlineMail } from "react-icons/hi";
import { IoIosPeople } from "react-icons/io";
import Mapper from "../components/home/Mapper";
import { contentHeader } from "../utils/header";
import EvictionsByYearChart from "../components/charts/EvictionsByYearChart";
import EvictionsByMonthChart from "../components/charts/EvictionByMonthChart";
import EvictionsAreaChart from "../components/charts/EvictionsAreaChart";
import EvictionsLineChart from "../components/charts/EvictionByYearLineChart";
import StackedEvictionsHistogram from "../components/charts/StackedEvictionsHistogram";
import HeatMapStats from "../components/charts/HeatMapStatsCard";
import EvictionReasonStatsCard from "../components/charts/EvictionReasonsStatsCard";
import EvictionReasonStatCard from "../components/charts/EvictionReasonsStatCard";
import HistogramChart from "../components/charts/HistogramChart";
import HeatmapChart from "../components/charts/HeatmapChart";
import TopEvictionsBarUpdated from "../components/charts/TopEvictionsBarUpdated";
import ReasonsYearLineChart from "../components/charts/ReasonsYearsLineChart";
import ReasonsMonthLineChart from "../components/charts/ReasonsMonthLineChart";
import GCPIcon from "../assets/logo-preview.png";
import Maintenance from "../components/home/Maintenance";
import InfrastructureDashboard from "../components/home/AmenitiesChart";

interface CityOption {
  label: string;
  value: string;
}

// Define the structure of the individual reason data
interface ReasonData {
  total_evictions_individuals: number;
  total_evictions_households: number;
}

interface VisitorStats {
  total_unique: number;
  new_today: number;
  visits_today: number;
}

// Define the top-level structure for evictions by reason per year
interface EvictionsByReasonsPerYear {
  [year: string]: {
    [reason: string]: ReasonData;
  };
}

type EvictionRecord = {
  evictedfromregion: string;
  evictedfromdistrict?: string;
  year: number;
  month: string;
  evictedindividuals: number;
  evictedhouseholds: number;
  evictedreason: string;
};

type ProcessedEvictionData = {
  [region: string]: {
    evictedindividuals: number;
    evictedhouseholds: number; // ✅ Add this line
    evictionsByRegionPerYear: { [year: number]: number };
    evictionsByRegionPerMonth: { [month: string]: number }; // ✅ Added this
    evictionsByYearPerMonth: Record<string, Record<string, number>>;
    preventionsByYearPerMonth: Record<string, Record<string, number>>;
    preventionsByRegionPerMonth: Record<string, number>;
    preventionsByYear?: { [year: string]: number };  // ✅ Added
    evictionsByReasonsPerYear: Record<string, Record<string, number>>; // ✅ Added this
    evictionsByRegionPerReason: Record<string, Record<string, number>>; // ✅ Added this
    evictionsPerRegionPerYearByReason: Record<
      string, // Year
      Record<
        string, // Reason
        number  // Eviction count
      >
    >;
  };
};
type ProcessedEvictionDataAggregate = {
  evictedindividuals: number;
  evictedhouseholds: number;
  totalProtectedIndividuals: number;
  evictionsByRegionPerYear: Record<string, { evictions: number }>; // For evictions by region per year
  evictionsByRegionPerYearUpdated: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For evictions by region per year
  evictionsByRegionPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For evictions by region per month
  evictionsByYearPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For evictions by year per month
  preventionsByYearPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For preventions by year per month
  preventionsByRegionPerMonth: Record<string, number>; // For preventions by region per month
  evictionsByReasonsPerYear: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For evictions by reason per year
  evictionsByReasonsPerMonth: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For evictions by reason per Month
  evictionsByRegionPerReason: Record<string, Record<string, number>>; // For evictions by region per reason
  evictionsByRegionPerYearByReason: Record<string, Record<string, Record<string, { total_evictions_individuals: number }>>>  // For evictions per region per year by reason
  preventionsByRegionPerYearUpdated: Record<string, Record<string, { total_evictions_individuals: number; total_evictions_households: number }>>; // For preventions by region per year
  overallEvictionsByYear: Record<string, number>; // For overall evictions by year
  overallEvictionsByMonth: Record<string, number>; // For overall evictions by year
  preventionsByRegionPerYear: Record<string, number>; // For preventions by region per year
  overallPreventionsByYear: Record<string, number>; // For overall preventions by year
  overallPreventionsByMonth: Record<string, number>; // For overall preventions by month
  overallEvictedRegions: Record<string, number>;
  overallEvictionReasons: Record<string, number>;
  overallEvictedDistricts: Record<string, number>;
  preventionsByRegionPerYearPerMonth: Record<string, // region
    Record<
      string, // year
      Record<
        string, // month (e.g., 'Jan', 'Feb')
        {
          total_preventions_individuals: number;
          total_preventions_households: number;
        }
      >
    >
  >;
  evictionsByRegionPerYearPerMonth: Record<string, // region
    Record<
      string, // year
      Record<
        string, // month (e.g., 'Jan', 'Feb')
        {
          total_evictions_individuals: number;
          total_evictions_households: number;
        }
      >
    >
  >;
};

interface HumanitarianSnapshot {
  population_in_need: number;
  population_target: number;
  population_reached: number;
  funding_required: number;
  funding_received: number;
  report_month: number;
  report_year: number;
}

interface EvictionReasonData {
  total_evictions_individuals: number;
  total_evictions_households: number;
}
type AggregatedEvictionData = Record<string, EvictionReasonData | number>;
type AggregatedData = Record<string, AggregatedEvictionData>;

interface RegionStats {
  evictedindividuals: number;
  evictedhouseholds: number;
  regionName?: string;
  evictionsByRegionPerYear: Record<string, { evictions: number }>;
  evictionsByRegionPerMonth: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  overallEvictionsByMonth: Record<string, number>;
  evictionsByYearPerMonth: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  preventionsByYearPerMonth: Record<
    string,
    Record<
      string,
      {
        total_evictions_individuals: number;
        total_evictions_households: number;
      }
    >
  >;
  preventionsByRegionPerMonth: Record<string, number>; // ✅ Add this line
  preventionsByRegionPerYear: Record<string, number>; // ✅ Add this line
}

const Home = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [tab, setTab] = useState<number>(1);
  const currentYear = new Date().getFullYear() - 1;
  const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [cities, setCities] = useState<CityOption[]>([]);
  const [totalEvictedIndividuals, setTotalEvictedIndividuals] = useState<number>(0);
  const [totalEvictedHouseholds, setTotalEvictedHouseholds] = useState<number>(0);
  const [topReasonsOverall, setTopReasonsOverall] = useState<[string, number][]>([]);
  const [allReasonsOverall, setAllReasonsOverall] = useState<[string, number][]>([]);
  const [allReasonsPerYear, setAllReasonsPerYear] = useState<Record<string, Record<string, number>>>({});
  const [allReasonsPerYearAgg, setAllReasonsPerYearAgg] = useState<Record<string, Record<string, number>>>({});
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [evictionStats, setEvictionStats] = useState<Record<string, RegionStats>>({});
  const [preventionStats, setPreventionStats] = useState<Record<string, RegionStats>>({});
  const [topDistrictsOverall, setTopDistrictsOverall] = useState<[string, number][]>([]); // Track top districts
  const [topRegionsOverall, setTopRegionsOverall] = useState<[string, number][]>([]); // Track top regions
  const [districtsOverall, setDistrictsOverall] = useState<[string, number][]>([]); // Track top districts
  const [regionsOverall, setRegionsOverall] = useState<[string, number][]>([]); // Track top regions
  const [evictionsByYear, setEvictionsByYear] = useState<[string, number][]>([]);
  const [evictionsByMonth, setEvictionsByMonth] = useState<[string, number][]>([]);
  const [yearSelected, setYearSelected] = useState<string | null>(null);
  const [preventionsByYear, setPreventionsByYear] = useState<[string, number][]>([]);
  const [preventionsByMonth, setPreventionsByMonth] = useState<[string, number][]>([]);
  const [allRegionsPerReason, setAllRegionsPerReason] = useState<Record<string, Record<string, number>>>({});
  const [totalProtectedIndividuals, setTotalProtectedIndividuals] = useState<number>(0);
  const normalizeRegionName = (name: string): string => {
    if (!name) return name;
    return name.replace(/["']/g, "").trim();
  };
  const [processedData, setProcessedData] = useState<ProcessedEvictionData>({});
  const [processedDataAggregate, setProcessedDataAggregate] = useState<ProcessedEvictionDataAggregate>({} as ProcessedEvictionDataAggregate);
  const [evictionStatsAPI, setEvictionStatsAPI] = useState<ProcessedEvictionData>({});
  const [evictionStatsAPIAggr, setEvictionStatsAPIAggr] = useState<ProcessedEvictionDataAggregate>();
  const [totalEvictedIndividualsAPI, setTotalEvictedIndividualsAPI] = useState(0);
  const [totalEvictedHouseholdsAPI, setTotalEvictedHouseholdsAPI] = useState(0);
  const [totalProtectedIndividualsAPI, setTotalProtectedIndividualsAPI] = useState(0);
  const [snapshot, setSnapshot] = useState<HumanitarianSnapshot | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedChart, setExpandedChart] = useState<"regions" | "districts" | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState<[string, number] | null>(null);
  const [stats, setStats] = useState<VisitorStats | null>(null);
  const [topYearStats, setTopYearStats] = useState<{
    topYears: { year: string; percent: number }[];
    dominantYear: string;
    dominantYearCount: number;
    yearOverYearChange: number;
    mostVolatileYear: string;
    mostVolatileChange: number;
    mostStableYear: string;
    mostStableChange: number;
    priorYear: number;
  }>({
    topYears: [],
    dominantYear: "",
    dominantYearCount: 0,
    yearOverYearChange: 0,
    mostVolatileYear: "",
    mostVolatileChange: 0,
    mostStableYear: "",
    mostStableChange: 0,
    priorYear: 0,
  });

  const [topReasonStats, setTopReasonStats] = useState<{
    topReasons: { reason: string; percent: number; count: number }[];
    dominantReason: string;
    yearOverYearChange: number;
    dominantReasonCount: number;
    mostVolatileReason: string;
    mostVolatileReasonChange: number;
    mostStableReason: string;
    mostStableReasonChange: number;
    priorYear: number;
  }>({
    topReasons: [],
    dominantReason: "",
    yearOverYearChange: 0,
    dominantReasonCount: 0,
    mostVolatileReason: "",
    mostVolatileReasonChange: 0,
    mostStableReason: "",
    mostStableReasonChange: 0,
    priorYear: 0,
  });
  // Initialize storage object
  const [monthlyStats, setMonthlyStats] = useState<{
    monthlyAverage: number;
    monthlyTrend: number;
    peakMonths: { month: string; count: number }[];
    lowestMonths: { month: string; count: number }[];
  }>({
    monthlyAverage: 0,
    monthlyTrend: 0,
    peakMonths: [],
    lowestMonths: []
  });

  const [isMaintenance, setIsMaintenance] = useState(false);

  const tabItems = [
    { id: 1, label: "Overview" },
    { id: 2, label: "Eviction Trends" },
    { id: 3, label: "Top Highlights" },
    { id: 4, label: "Reason Trends" },
    { id: 5, label: "Regional Reasons" },
    { id: 6, label: "Monthly Map" },
    { id: 7, label: "Reason Timeline" },
  ];

  const tabClass = (current: number) =>
    `px-3 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-xs sm:text-sm font-medium rounded-t-lg
  transition-all duration-200 w-full sm:w-auto text-center
  ${tab === current
      ? "text-[#64748B] bg-white shadow-sm"
      : "text-[#64748B] border-transparent hover:text-[#F97316] hover:bg-white/50"
    }`;


  const processEvictionDataAggreate = (apiData: any): ProcessedEvictionDataAggregate => { // apiData should be an object, not an array
    const aggregatedData: ProcessedEvictionDataAggregate = {
      evictedindividuals: 0,
      evictedhouseholds: 0,
      evictionsByRegionPerYear: {},
      evictionsByRegionPerMonth: {},
      evictionsByYearPerMonth: {},
      preventionsByYearPerMonth: {},
      preventionsByRegionPerMonth: {},
      evictionsByReasonsPerYear: {},
      evictionsByReasonsPerMonth: {},
      evictionsByRegionPerReason: {},
      evictionsByRegionPerYearByReason: {},
      evictionsByRegionPerYearUpdated: {},
      overallEvictionsByYear: {},
      overallEvictionsByMonth: {},
      preventionsByRegionPerYear: {},
      overallPreventionsByYear: {},
      overallPreventionsByMonth: {},
      overallEvictedRegions: {},
      overallEvictionReasons: {},
      overallEvictedDistricts: {},
      totalProtectedIndividuals: 0,
      evictionsByRegionPerYearPerMonth: {},
      preventionsByRegionPerYearPerMonth: {},
      preventionsByRegionPerYearUpdated: {},
    };

    // Assuming apiData is an object, we can destructure it as follows
    const {
      totalEvictionsIndividuals,
      totalEvictionsHouseholds,
      evictionsByRegionPerYear,
      evictionsByRegionPerMonth,
      evictionsByYearPerMonth,
      preventionsByYearPerMonth,
      preventionsByRegionPerMonth,
      evictionsByReasonsPerYear,
      evictionsByReasonsPerMonth,
      evictionsByRegionPerReason,
      evictionsByRegionPerYearByReason,
      evictionsByRegionPerYearUpdated,
      overallEvictionsByYear,
      overallEvictionsByMonth,
      preventionsByRegionPerYear,
      overallPreventionsByYear,
      overallPreventionsByMonth,
      overallEvictedRegions,
      overallEvictionReasons,
      overallEvictedDistricts,
      totalProtectedIndividuals,
      preventionsByRegionPerYearPerMonth,
      evictionsByRegionPerYearPerMonth,
      preventionsByRegionPerYearUpdated,
    } = apiData;

    // Set the values as received from the API without aggregation
    aggregatedData.evictedindividuals = totalEvictionsIndividuals;
    aggregatedData.evictedhouseholds = totalEvictionsHouseholds;
    aggregatedData.evictionsByRegionPerYear = evictionsByRegionPerYear;
    aggregatedData.evictionsByRegionPerMonth = evictionsByRegionPerMonth;
    aggregatedData.evictionsByYearPerMonth = evictionsByYearPerMonth; // Assuming this is correct
    aggregatedData.preventionsByYearPerMonth = preventionsByYearPerMonth;
    aggregatedData.preventionsByRegionPerMonth = preventionsByRegionPerMonth;
    aggregatedData.evictionsByReasonsPerYear = evictionsByReasonsPerYear;
    aggregatedData.evictionsByReasonsPerMonth = evictionsByReasonsPerMonth;
    aggregatedData.evictionsByRegionPerReason = evictionsByRegionPerReason;
    aggregatedData.evictionsByRegionPerYearByReason = evictionsByRegionPerYearByReason;
    aggregatedData.overallEvictionsByYear = overallEvictionsByYear;
    aggregatedData.overallEvictionsByMonth = overallEvictionsByMonth;
    aggregatedData.preventionsByRegionPerYear = preventionsByRegionPerYear;
    aggregatedData.overallPreventionsByYear = overallPreventionsByYear;
    aggregatedData.overallPreventionsByMonth = overallPreventionsByMonth;
    aggregatedData.overallEvictedRegions = overallEvictedRegions;
    aggregatedData.overallEvictionReasons = overallEvictionReasons;
    aggregatedData.overallEvictedDistricts = overallEvictedDistricts;
    aggregatedData.totalProtectedIndividuals = totalProtectedIndividuals;
    aggregatedData.evictionsByRegionPerYearUpdated = evictionsByRegionPerYearUpdated;
    aggregatedData.evictionsByRegionPerYearPerMonth = evictionsByRegionPerYearPerMonth;
    aggregatedData.preventionsByRegionPerYearPerMonth = preventionsByRegionPerYearPerMonth;
    aggregatedData.preventionsByRegionPerYearUpdated = preventionsByRegionPerYearUpdated;

    setTotalEvictedIndividualsAPI(totalEvictionsIndividuals);
    setTotalProtectedIndividuals(totalProtectedIndividuals)
    setTotalEvictedHouseholdsAPI(totalEvictionsHouseholds);
    setProcessedDataAggregate(aggregatedData);


    const formattedEvictionsByReasonsPerYear: Record<string, Record<string, number>> =
      Object.entries(aggregatedData.evictionsByReasonsPerYear).reduce((acc, [year, reasons]) => {
        acc[year] = {}; // Initialize empty object for each year

        Object.entries(reasons).forEach(([reason, data]) => {
          // Type guard: Check if data is an object with the expected properties
          if (isEvictionReasonData(data)) {
            // Only add the count of individuals to the aggregated data
            acc[year][reason] = data.total_evictions_individuals;
          } else {
            // If data is a number, just use it directly
            acc[year][reason] = data as number;
          }
        });

        return acc;
      }, {} as Record<string, Record<string, number>>);

    // Type guard function to check if data is of type EvictionReasonData
    function isEvictionReasonData(data: EvictionReasonData | number): data is EvictionReasonData {
      return (data as EvictionReasonData).total_evictions_individuals !== undefined;
    }

    // Now, set the state with the new structure
    setAllReasonsPerYearAgg(formattedEvictionsByReasonsPerYear);

    const formattedEvictionsByRegionPerReason: Record<string, Record<string, number>> =
      Object.entries(aggregatedData.evictionsByRegionPerReason).reduce((acc, [region, reasons]) => {
        acc[region] = {}; // Initialize empty object for each region

        Object.entries(reasons).forEach(([reason, data]) => {
          // Type guard: Check if data is an object with the expected properties
          if (isEvictionReasonsData(data)) {
            // Only add the count of individuals to the aggregated data
            acc[region][reason] = data.total_evictions_individuals;
          } else {
            // If data is a number, just use it directly
            acc[region][reason] = data as number;
          }
        });

        return acc;
      }, {} as Record<string, Record<string, number>>);

    // Type guard function to check if data is of type EvictionReasonData
    function isEvictionReasonsData(data: EvictionReasonData | number): data is EvictionReasonData {
      return (data as EvictionReasonData).total_evictions_individuals !== undefined;
    }

    // Now, set the state with the new structure
    setAllRegionsPerReason(formattedEvictionsByRegionPerReason);

    // Compute the top 5 eviction regions based on evicted individuals
    const sortedOverallRegions = Object.entries(aggregatedData.overallEvictedRegions)
      .sort((a, b) => b[1] - a[1]) // Sort by evicted individuals (Descending)
      .slice(0, 5) // Get top 5 regions
      .sort((a, b) => a[0].localeCompare(b[0])); // Sort alphabetically by region name

    setTopRegionsOverall(sortedOverallRegions);
    setRegionsOverall(
      Object.entries(aggregatedData.overallEvictedRegions)
        .sort((a, b) => b[1] - a[1]) // Sort by number of evicted individuals   
    )


    // Compute the top 5 eviction reasons overall (sorted by frequency)
    const sortedOverallReasons = Object.entries(aggregatedData.overallEvictionReasons)
      .sort((a, b) => b[1] - a[1]) // Sort by frequency
      .slice(0, 5) // Get top 5
      .sort((a, b) => a[0].localeCompare(b[0])); // Sort alphabetically by name
    setTopReasonsOverall(sortedOverallReasons);

    // Compute the top 5 districts based on evicted individuals
    const sortedOverallDistricts = Object.entries(aggregatedData.overallEvictedDistricts)
      .sort((a, b) => b[1] - a[1]) // Sort by number of evicted individuals
      .slice(0, 5) // Get top 5 districts
      .sort((a, b) => a[0].localeCompare(b[0])); // Sort alphabetically by district name

    setTopDistrictsOverall(sortedOverallDistricts);
    setDistrictsOverall(
      Object.entries(aggregatedData.overallEvictedDistricts)
        .filter(([_, value]) => value > 0) // ✅ keep only non-zero
        .sort((a, b) => b[1] - a[1]) // Sort by number of evicted individuals   
    )


    // 🔹 Store overall evictions per month in state (NEW)
    setEvictionsByMonth(
      Object.entries(aggregatedData.overallEvictionsByMonth)
        .filter(([month]) => month !== "Unknown") // Remove "Unknown"
        .sort((a, b) => new Date(`1 ${a[0]} 2000`).getMonth() - new Date(`1 ${b[0]} 2000`).getMonth()) // Sort months correctly
    );

    // 🔹 Store overall evictions per year in state (NEW)
    setEvictionsByYear(Object.entries(aggregatedData.overallEvictionsByYear).sort((a, b) => a[0].localeCompare(b[0])));
    //console.log("Aggregated Data:", aggregatedData);

    // 🔹 Store overall preventins per month in state (NEW)
    setPreventionsByMonth(
      Object.entries(aggregatedData.overallPreventionsByMonth)
        .filter(([month]) => month !== "Unknown")
        .sort(
          (a, b) =>
            new Date(`1 ${a[0]} 2000`).getMonth() - new Date(`1 ${b[0]} 2000`).getMonth()
        )
    );
    // 🔹 Store overall preventions per year in state (NEW)
    setPreventionsByYear(Object.entries(aggregatedData.overallPreventionsByYear).sort((a, b) => a[0].localeCompare(b[0])));

    // 🔹 Store overall preventions per region in state (NEW)

    const aggregatedEvictionsPerYear: Record<string, number> = aggregatedData.overallEvictionsByYear;

    let dominantYear = "";
    let dominantYearCount = 0;
    let yearOverYearChange = 0;
    let mostVolatileYear = "";
    let mostVolatileChange = 0;
    let mostStableYear = "";
    let mostStableChange = Infinity;

    // Get total evictions to calculate percentages
    const totalEvictions = Object.values(aggregatedEvictionsPerYear).reduce((sum, count) => sum + count, 0);

    // Sort years in ascending order
    const sortedYears = Object.keys(aggregatedEvictionsPerYear).sort((a, b) => Number(a) - Number(b));

    // Get the latest and previous year
    const latestYear = sortedYears[sortedYears.length - 1];
    const previousYear = sortedYears[sortedYears.length - 2];

    if (latestYear && previousYear) {
      const latestCount = aggregatedEvictionsPerYear[latestYear];
      const previousCount = aggregatedEvictionsPerYear[previousYear];

      if (previousCount) {
        yearOverYearChange = ((latestCount - previousCount) / previousCount) * 100;
      }
    }

    // Compute top years by share of total evictions
    const computedTopYears = Object.entries(aggregatedEvictionsPerYear)
      .map(([year, count]) => {
        if (count > dominantYearCount) {
          dominantYear = year;
          dominantYearCount = count;
        }

        return {
          year,
          percent: totalEvictions ? parseFloat(((count / totalEvictions) * 100).toFixed(1)) : 0
        };
      })
      .sort((a, b) => b.percent - a.percent)
      .slice(0, 3);

    // Find the most volatile & stable years
    for (let i = 1; i < sortedYears.length; i++) {
      const currentYear = sortedYears[i];
      const previousYear = sortedYears[i - 1];

      const currentCount = aggregatedEvictionsPerYear[currentYear];
      const previousCount = aggregatedEvictionsPerYear[previousYear];

      if (previousCount > 0) {
        const change = ((currentCount - previousCount) / previousCount) * 100;

        // Identify the most volatile year (largest absolute change)
        if (Math.abs(change) > Math.abs(mostVolatileChange)) {
          mostVolatileYear = currentYear;
          mostVolatileChange = change;
        }

        // Identify the most stable year (smallest absolute change)
        if (Math.abs(change) < Math.abs(mostStableChange)) {
          mostStableYear = currentYear;
          mostStableChange = change;
        }
      }
    }

    // Update state
    setTopYearStats({
      topYears: computedTopYears,
      dominantYear,
      dominantYearCount,
      yearOverYearChange: parseFloat(yearOverYearChange.toFixed(1)), // Rounded to 1 decimal
      mostVolatileYear,
      mostVolatileChange: parseFloat(mostVolatileChange.toFixed(1)),
      mostStableYear,
      mostStableChange: parseFloat(mostStableChange.toFixed(1)),
      priorYear: Number(previousYear) || 0
    });

    const data: EvictionsByReasonsPerYear = aggregatedData.evictionsByReasonsPerYear;

    let dominantReason = "";
    let yearOverYearChangeReason = 0;
    let dominantReasonCount = 0;
    let mostVolatileReason = "";
    let mostVolatileReasonChange = -Infinity;  // Start with a very low number to capture largest change
    let mostStableReason = "";
    let mostStableReasonChange = Infinity;  // Start with a very high number to capture smallest change

    let computedTopReasons: { reason: string; percent: number; count: number }[] = [];

    const aggregateReasonsPerYear: Record<string, Record<string, number>> = {};

    Object.entries(data).forEach(([year, reasonsObj]) => {
      aggregateReasonsPerYear[year] = {};

      Object.entries(reasonsObj).forEach(([reason, value]) => {
        if (
          value &&
          typeof value.total_evictions_individuals === "number" &&
          !isNaN(value.total_evictions_individuals)
        ) {
          aggregateReasonsPerYear[year][reason.trim()] = value.total_evictions_individuals;
        }
      });
    });

    const allYears = Object.keys(aggregateReasonsPerYear).map(Number).sort((a, b) => a - b);
    const latestYears = allYears[allYears.length - 1];
    const previousYears = allYears.length > 1 ? allYears[allYears.length - 2] : null;

    if (latestYears && previousYears !== null) {
      const latestData = aggregateReasonsPerYear[latestYears] || {};
      const prevData = aggregateReasonsPerYear[previousYears] || {};

      const totalEvictionsOverall = Object.values(latestData).reduce((sum, count) => sum + count, 0) || 1;

      computedTopReasons = Object.entries(latestData)
        .map(([reason, count]) => {
          if (count > dominantReasonCount) {
            dominantReason = reason;
            dominantReasonCount = count;
          }

          return {
            reason,
            percent: parseFloat(((count / totalEvictionsOverall) * 100).toFixed(1)),
            count
          };
        })
        .sort((a, b) => b.percent - a.percent)
        .slice(0, 3);

      let fallbackVolatileReason = computedTopReasons[0]?.reason || "N/A";
      let fallbackStableReason = computedTopReasons[computedTopReasons.length - 1]?.reason || "N/A";

      // Loop to check for volatility and stability
      Object.entries(latestData).forEach(([reason, latestCount]) => {
        const previousCount = prevData[reason] || 0;

        if (previousCount === 0 && latestCount === 0) return;

        let change = 0;
        if (previousCount > 0) {
          change = ((latestCount - previousCount) / previousCount) * 100;
        } else if (previousCount === 0 && latestCount > 0) {
          // If no previous data but there is data now, consider it a 100% increase (or some arbitrary factor).
          change = 100; // You can adjust this factor as needed.
        }

        // Capture the most volatile reason by largest absolute percentage change (whether positive or negative)
        if (Math.abs(change) > Math.abs(mostVolatileReasonChange)) {
          mostVolatileReason = reason;
          mostVolatileReasonChange = change;
        }

        // Identify the most stable reason (smallest absolute change)
        if (Math.abs(change) < Math.abs(mostStableReasonChange)) {
          mostStableReason = reason;
          mostStableReasonChange = change;
        }
      });

      if (!mostVolatileReason) {
        mostVolatileReason = fallbackVolatileReason;
        mostVolatileReasonChange = 0;
      }

      if (!mostStableReason) {
        mostStableReason = fallbackStableReason;
        mostStableReasonChange = 0;
      }

      // Compute YoY change for dominant reason only
      if (dominantReason) {
        const normDominant = dominantReason.trim();
        const latestCount = latestData[normDominant] ?? 0;
        const prevCount = prevData[normDominant] ?? 0;

        if (prevCount > 0) {
          yearOverYearChangeReason = ((latestCount - prevCount) / prevCount) * 100;
        } else if (latestCount > 0) {
          yearOverYearChangeReason = (latestCount / 5) * 100; // Similar handling for first-time data
        }

        yearOverYearChangeReason = parseFloat(yearOverYearChangeReason.toFixed(1));
      }
    }

    setTopReasonStats({
      topReasons: computedTopReasons,
      dominantReason,
      yearOverYearChange: yearOverYearChangeReason,
      dominantReasonCount,
      mostVolatileReason,
      mostVolatileReasonChange: parseFloat(mostVolatileReasonChange.toFixed(1)),
      mostStableReason,
      mostStableReasonChange: parseFloat(mostStableReasonChange.toFixed(1)),
      priorYear: previousYears || 0
    });


    // 🔥 Heatmap Stats Calculation Based on API Format

    const aggregatedEvictionsByMonth: Record<string, number> = {}; // Total evictions per month across all regions

    // 🔹 Aggregate individual evictions from all regions and months
    Object.values(aggregatedData.evictionsByRegionPerMonth).forEach(regionMonthData => {
      Object.entries(regionMonthData).forEach(([month, value]) => {
        const count = value.total_evictions_individuals;
        aggregatedEvictionsByMonth[month] = (aggregatedEvictionsByMonth[month] || 0) + count;
      });
    });

    // ✅ Monthly breakdown
    const monthlyEvictions = Object.entries(aggregatedEvictionsByMonth).map(([month, count]) => ({
      month,
      count,
    }));

    // ✅ Sorted by eviction count descending
    const sortedMonths = [...monthlyEvictions].sort((a, b) => b.count - a.count);

    // ✅ Monthly average (assuming 12 months)
    const totalHEvictions = monthlyEvictions.reduce((sum, entry) => sum + entry.count, 0);
    const monthlyAverage = totalHEvictions / 12;

    // ✅ Monthly trend: compare latest 2 months by calendar order
    const sortedByMonthOrder = Object.keys(aggregatedEvictionsByMonth).sort(
      (a, b) => new Date(`01 ${a} 2000`).getMonth() - new Date(`01 ${b} 2000`).getMonth()
    );

    const latestMonth = sortedByMonthOrder[sortedByMonthOrder.length - 1];
    const previousMonth = sortedByMonthOrder[sortedByMonthOrder.length - 2];

    let monthlyTrend = 0;
    if (latestMonth && previousMonth) {
      const latestCount = aggregatedEvictionsByMonth[latestMonth];
      const previousCount = aggregatedEvictionsByMonth[previousMonth];
      if (previousCount > 0) {
        monthlyTrend = ((latestCount - previousCount) / previousCount) * 100;
      }
    }

    // ✅ Peak & lowest eviction months
    const peakMonths = sortedMonths.slice(0, 3);
    const lowestMonths = sortedMonths.slice(-3).reverse();


    // ✅ Final result object
    const monthlyStats = {
      monthlyAverage: Math.round(monthlyAverage),
      monthlyTrend: parseFloat(monthlyTrend.toFixed(1)),
      peakMonths: peakMonths.map(({ month, count }) => ({ month, count })),
      lowestMonths: lowestMonths.map(({ month, count }) => ({ month, count })),
    };
    setMonthlyStats(monthlyStats);

    return aggregatedData;

  };

  const fetchEvictionDataAggregate = async (): Promise<any[]> => {
    try {
      const response = await fetch(`${BASE_URL}/prod_eviction_stats/`); // Replace with your API endpoint
      if (!response.ok) throw new Error("Failed to fetch eviction data");

      return await response.json(); // Ensure it returns an array of records
    } catch (error) {
      console.error("Error fetching eviction data:", error);
      return []; // Return an empty array to avoid breaking the app
    }
  };


  useEffect(() => {
    const fetchAndProcessDataAggregate = async () => {
      try {
        // Fetch only eviction data
        const evictionData = await fetchEvictionDataAggregate();
        //console.log("Eviction Data from API:", evictionData);

        // Process the eviction data
        const processedEvictions = processEvictionDataAggreate(evictionData);

        // Log the processed eviction data
        //console.log("Processed Eviction Stats:", processedEvictions);

        setEvictionStatsAPIAggr(processedEvictions);
        // Optionally, update the state with the processed eviction data
      } catch (error) {
        console.error("Error fetching eviction data:", error);
        setIsMaintenance(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAndProcessDataAggregate();
  }, []); // Run only once on mount

  useEffect(() => {
    const fetchSnapshot = async () => {
      try {
        const response = await fetch(`${BASE_URL}/latest-humanitarian-snapshot/`);
        if (!response.ok) throw new Error("Failed to fetch snapshot");

        const data = await response.json();
        setSnapshot(data);
      } catch (err: any) {
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchSnapshot();
  }, []);

  useEffect(() => {
    if (evictionStatsAPIAggr) {
      //console.log("Updated Eviction Stats from state:", evictionStatsAPIAggr);
    }
  }, [evictionStatsAPIAggr]);  // Runs every time `evictionStatsAPIAggr` updates

  // Function to format numbers for display
  const formatNumber = (num: number) => num.toLocaleString();

  // Function to update stats when a region is clicked
  const handleRegionClick = (region: string, stats: RegionStats) => {
    setSelectedRegion(region);
  };

  const displayData = selectedRegion && evictionStatsAPI[selectedRegion]
    ? evictionStatsAPI[selectedRegion]
    : {
      evictedindividuals: totalEvictedIndividualsAPI,
      evictedhouseholds: totalEvictedHouseholdsAPI
    };

  const displayPreventions = selectedRegion && evictionStatsAPI[selectedRegion]
    ? evictionStatsAPI[selectedRegion]
    : {
      protectedindividuals: totalProtectedIndividualsAPI, // ✅ Explicitly add protectedindividuals
    };

  const displayEvictionDataByYear = (() => {
    if (selectedRegion) {


      const regionData = processedDataAggregate?.evictionsByRegionPerYearUpdated?.[selectedRegion];

      if (regionData) {

        // Handle both cases: direct numbers OR nested objects
        const sortedRegionData: [string, number][] = Object.entries(regionData).map(
          ([year, value]) => {
            // If the value is a number (direct count), use it directly
            if (typeof value === 'number') {
              return [year, value];
            }
            // If the value is an object with `total_evictions_individuals`, use that
            else if (typeof value === 'object' && value !== null && 'total_evictions_individuals' in value) {
              return [year, value.total_evictions_individuals];
            }
            // Fallback for invalid data (shouldn't happen if data is correct)
            console.warn(`Unexpected data format for year ${year}:`, value);
            return [year, 0]; // Default to 0 if structure is invalid
          }
        );

        return sortedRegionData;
      } else {

        return [];
      }
    } else {
      // For overall data (already in correct format)
      const overallData: [string, number][] = Object.entries(processedDataAggregate?.overallEvictionsByYear || {})
        .map(([year, totalEvictions]) => [String(year), totalEvictions]);
      return overallData;
    }
  })();

  const displayPreventionDataByYear = (() => {
    if (selectedRegion) {
      const regionData = processedDataAggregate?.preventionsByRegionPerYearUpdated?.[selectedRegion];

      if (regionData) {
        const sortedRegionData: [string, number][] = Object.entries(regionData).map(
          ([year, value]) => {
            if (typeof value === 'number') {
              return [year, value];
            }
            else if (typeof value === 'object' && value !== null && 'total_evictions_individuals' in value) {
              return [year, value.total_evictions_individuals];
            }
            return [year, 0];
          }
        );
        return sortedRegionData;
      } else {
        return [];
      }
    } else {
      const overallData: [string, number][] = Object.entries(processedDataAggregate?.overallPreventionsByYear || {})
        .map(([year, totalPreventions]) => [String(year), totalPreventions]);
      return overallData;
    }
  })();
  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const displayEvictionDataByMonth = (() => {
    const regionalData = selectedRegion
      ? processedDataAggregate?.evictionsByRegionPerMonth?.[selectedRegion]
      : null;

    const overallData = processedDataAggregate?.overallEvictionsByMonth;

    const hasData = (data: unknown): data is Record<string, any> =>
      !!data && typeof data === "object" && Object.keys(data).length > 0;

    if (selectedRegion && hasData(regionalData)) {
      return Object.entries(regionalData)
        .map(([month, data]) => [
          month,
          (data as { total_evictions_individuals: number }).total_evictions_individuals,
        ] as [string, number])
        .sort((a, b) => monthOrder.indexOf(a[0]) - monthOrder.indexOf(b[0]));
    }

    if (hasData(overallData)) {
      return Object.entries(overallData)
        .map(([month, evictions]) => [month, evictions as number] as [string, number])
        .sort((a, b) => monthOrder.indexOf(a[0]) - monthOrder.indexOf(b[0]));
    }

    return [];
  })();


  const displayPreventionsDataByMonth = (() => {
    const regionData = selectedRegion
      ? processedDataAggregate?.preventionsByRegionPerMonth?.[selectedRegion]
      : null;

    const overallData = processedDataAggregate?.overallPreventionsByMonth;

    const hasData = (data: unknown): data is Record<string, any> =>
      !!data && typeof data === "object" && Object.keys(data).length > 0;

    if (selectedRegion && hasData(regionData)) {
      return Object.entries(regionData)
        .map(([month, data]) => [
          month,
          (data as { total_evictions_individuals: number }).total_evictions_individuals,
        ] as [string, number])
        .sort((a, b) => monthOrder.indexOf(a[0]) - monthOrder.indexOf(b[0]));
    }

    if (hasData(overallData)) {
      return Object.entries(overallData)
        .map(([month, preventions]) => [month, preventions as number] as [string, number])
        .sort((a, b) => monthOrder.indexOf(a[0]) - monthOrder.indexOf(b[0]));
    }

    return [];
  })();



  function transformToStats(
    input: ProcessedEvictionDataAggregate | undefined
  ): Record<string, {
    evictionsByRegionPerMonth: Record<string, number>;
    evictionsByYearPerMonth: Record<string, Record<string, number>>;
    preventionsByYearPerMonth: Record<string, Record<string, number>>;
    preventionsByRegionPerMonth: Record<string, number>;
  }> {
    if (!input) return {};

    // Simplify evictionsByRegionPerMonth to just total evictions for each region
    const evictionsByRegionPerMonth = Object.fromEntries(
      Object.entries(input.evictionsByRegionPerMonth || {}).map(([region, months]) => [
        region,
        Object.values(months).reduce((total, evData) => total + evData.total_evictions_individuals, 0),
      ])
    );

    const evictionsByYearPerMonth: Record<string, Record<string, number>> = {};
    for (const [year, months] of Object.entries(input.evictionsByYearPerMonth || {})) {
      evictionsByYearPerMonth[year] = {};
      for (const [month, evData] of Object.entries(months)) {
        evictionsByYearPerMonth[year][month] = evData.total_evictions_individuals;
      }
    }

    const preventionsByYearPerMonth: Record<string, Record<string, number>> = {};
    for (const [year, months] of Object.entries(input.preventionsByYearPerMonth || {})) {
      preventionsByYearPerMonth[year] = {};
      for (const [month, data] of Object.entries(months)) {
        preventionsByYearPerMonth[year][month] = data.total_evictions_individuals;
      }
    }

    return {
      [new Date().getFullYear().toString()]: {
        evictionsByRegionPerMonth,
        evictionsByYearPerMonth,
        preventionsByYearPerMonth,
        preventionsByRegionPerMonth: input.preventionsByRegionPerMonth || {},
      },
    };
  }

  const extractIndividuals = (
    data: Record<string, Record<string, { total_evictions_individuals: number }>>
  ): Record<string, Record<string, number>> =>
    Object.fromEntries(
      Object.entries(data).map(([year, months]) => [
        year,
        Object.fromEntries(
          Object.entries(months).map(([month, val]) => [month, val.total_evictions_individuals])
        ),
      ])
    );

  const extractRegionEvictions = (
    data: Record<string, Record<string, Record<string, { total_evictions_individuals: number }>>>
  ): Record<string, Record<string, Record<string, number>>> =>
    Object.fromEntries(
      Object.entries(data).map(([region, yearData]) => [
        region,
        Object.fromEntries(
          Object.entries(yearData).map(([year, months]) => [
            year,
            Object.fromEntries(
              Object.entries(months).map(([month, val]) => [month, val.total_evictions_individuals])
            ),
          ])
        ),
      ])
    );

  const extractRegionEvictionsByMonth = (
    data: Record<string, Record<string, Record<string, { total_evictions_individuals: number }>>>
  ): Record<string, Record<string, Record<string, number>>> =>
    Object.fromEntries(
      Object.entries(data).map(([region, yearData]) => [
        region,
        Object.fromEntries(
          Object.entries(yearData).map(([year, months]) => [
            year,
            Object.fromEntries(
              Object.entries(months).map(([month, val]) => [month, val.total_evictions_individuals])
            ),
          ])
        ),
      ])
    );

  const extractRegionPreventionsByMonth = (
    data: Record<string, Record<string, Record<string, { total_preventions_individuals: number }>>>
  ): Record<string, Record<string, Record<string, number>>> =>
    Object.fromEntries(
      Object.entries(data).map(([region, yearData]) => [
        region,
        Object.fromEntries(
          Object.entries(yearData).map(([year, months]) => [
            year,
            Object.fromEntries(
              Object.entries(months).map(([month, val]) => [month, val.total_preventions_individuals])
            ),
          ])
        ),
      ])
    );

  function formatNumberShort(amount: number): string {
    if (amount >= 1_000_000) {
      return `${(amount / 1_000_000).toFixed(1)}M`;
    } else if (amount >= 1_000) {
      return `${(amount / 1_000).toFixed(1)}K`;
    } else {
      return amount.toLocaleString();
    }
  }

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${BASE_URL}/visitor-stats/`);
        if (!res.ok) throw new Error("Failed to load stats");
        const data: VisitorStats = await res.json();
        setStats(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center min-h-screen">Loading...</div>;
  }

  if (isMaintenance) {
    return <Maintenance />;
  }

  return (
    <div className="md:col-span-2 sm:col-span-1 w-full">
      <div className="relative"> {/* Added wrapper for scroll containment */}
        {/* Scrollable container */}
        <div className="overflow-x-auto hide-scrollbar pb-1">
          <div className="grid grid-cols-7 shadow-md rounded-md w-max min-w-full">
            {tabItems.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`
              ${tabClass(id)} 
              px-3 py-2 text-xs sm:text-sm 
              whitespace-nowrap
            `}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content area below tabs */}
      <div className="mt-4 p-4 rounded-md bg-white shadow-sm">
        {tab === 1 && (
          <div>
            {/* Eviction Stats */}
            <div className="grid md:grid-cols-3 sm:grid-cols-1 gap-2 mb-2">
              <div className="flex justify-between shadow-md bg-white rounded p-3">
                <div>
                  <p className="text-3xl mb-2 font-bold text-dark">{formatNumber(displayData.evictedindividuals)}</p>
                  <p className="font-medium text-orange">Overall Evictions</p>
                </div>
                <div className="flex items-center">
                  <div className="p-4 text-white text-3xl rounded bg-orange">
                    <GoArrowUpRight />
                  </div>
                </div>
              </div>
              <div className="flex justify-between shadow-md bg-white rounded p-3">
                <div>
                  <p className="text-3xl mb-2 font-bold text-dark">{formatNumber(displayData.evictedhouseholds)}</p>
                  <p className="font-medium text-orange">Evicted Households</p>
                </div>
                <div className="flex items-center">
                  <div className="p-4 text-white text-3xl rounded bg-orange">
                    <GoShield />
                  </div>
                </div>
              </div>
              <div className="flex justify-between shadow-md bg-white rounded p-3">
                <div>
                  <p className="text-3xl mb-2 font-bold text-dark">{formatNumber(totalProtectedIndividuals)}</p>
                  <p className="font-medium text-orange">Preventive Measures</p>
                </div>
                <div className="flex items-center">
                  <div className="p-4 text-white text-3xl rounded bg-orange">
                    <GoPeople />
                  </div>
                </div>
              </div>
            </div>
            <InfrastructureDashboard />
            {/* Main Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4">
              {/* Left (25%) */}
              <div className="bg-red-400 p-4 text-center rounded-lg shadow-md md:col-span-3 w-full">
                <Mapper onRegionClick={handleRegionClick} />
              </div>

              {/* Center (60%) */}
              <div className="relative bg-blue-400 p-2 text-left rounded-lg shadow-md md:col-span-6 flex flex-col w-full">
                {/* Main Chart Section */}


                {/* Chart 1: Takes 50% of height */}
                <div className="w-full">
                  <EvictionsByYearChart
                    evictionDataByYear={displayEvictionDataByYear}
                    preventionDataByYear={displayPreventionDataByYear}
                    onYearSelect={setYearSelected}
                    selectedRegion={selectedRegion}
                  />
                </div>

                {/* Chart 2: Takes 50% of height */}
                <div className="w-full mt-4">
                  <EvictionsByMonthChart
                    evictionsByMonth={displayEvictionDataByMonth}
                    preventionDataByMonth={displayPreventionsDataByMonth}
                    yearSelected={yearSelected}
                    selectedRegion={selectedRegion}
                    evictionsByYearPerMonth={
                      processedDataAggregate?.evictionsByYearPerMonth
                        ? extractIndividuals(processedDataAggregate.evictionsByYearPerMonth)
                        : {}
                    }
                    evictionsByRegionPerYearPerMonth={
                      processedDataAggregate?.evictionsByRegionPerYearPerMonth
                        ? extractRegionEvictionsByMonth(processedDataAggregate.evictionsByRegionPerYearPerMonth)
                        : {}
                    }
                    preventionsByYearPerMonth={
                      processedDataAggregate?.preventionsByYearPerMonth
                        ? extractIndividuals(processedDataAggregate.preventionsByYearPerMonth)
                        : {}
                    }
                    preventionsByRegionPerYearPerMonth={
                      processedDataAggregate?.preventionsByRegionPerYearPerMonth
                        ? extractRegionPreventionsByMonth(processedDataAggregate.preventionsByRegionPerYearPerMonth)
                        : {}
                    }
                  />
                </div>
              </div>

              {/* Right (15%) - Optional Summary Panel */}
              <div className="p-3 rounded-lg shadow-sm border border-[#E5E7EB] w-full flex flex-col gap-3 bg-white md:col-span-3">
                {/* Header */}
                <div className="pb-1.5 border-b border-[#F3F4F6]">
                  <p className="text-[15px] font-bold text-[#111827] flex items-center gap-1">
                    Somalia HLP AoR
                  </p>
                </div>

                {/* PiN Section */}
                <div className="space-y-3">
                  <p className="text-[12px] font-semibold text-[#2D3748]">Population in Need</p>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Population in Need */}
                    <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] text-center flex flex-col items-center space-y-1.5">
                      <p className="text-[10px] text-[#4A5568]">PiN</p>
                      <div className="text-[28px] text-[#4A5568] my-0.5"><FaPeopleLine /></div>
                      <p className="text-[14px] font-bold text-[#1A202C]">
                        {snapshot
                          ? formatNumberShort(Number(snapshot.population_in_need))
                          : <span className="text-xs text-gray-400">Loading...</span>}
                      </p>
                    </div>

                    {/* Target Population */}
                    <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] text-center flex flex-col items-center space-y-1.5">
                      <p className="text-[10px] text-[#4A5568]">Target</p>
                      <div className="text-[28px] text-[#4A5568] my-0.5"><FaArrowsDownToPeople /></div>
                      <p className="text-[14px] font-bold text-[#1A202C]">
                        {snapshot
                          ? formatNumberShort(Number(snapshot.population_target))
                          : <span className="text-xs text-gray-400">Loading...</span>}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Funding Section */}
                <div className="space-y-3">
                  <p className="text-[12px] font-semibold text-[#2D3748]">Funding (USD)</p>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Required Funding */}
                    <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] text-center flex flex-col items-center space-y-1.5">
                      <p className="text-[10px] text-[#4A5568]">Required</p>
                      <div className="text-[28px] text-[#4A5568] my-0.5"><FaSackDollar /></div>
                      <p className="text-[14px] font-bold text-[#1A202C]">
                        {snapshot
                          ? `$${formatNumberShort(Number(snapshot.funding_required))}`
                          : <span className="text-xs text-gray-400">Loading...</span>}
                      </p>
                    </div>

                    {/* Received Funding */}
                    <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] text-center flex flex-col items-center space-y-1.5">
                      <p className="text-[10px] text-[#4A5568]">Received</p>
                      <div className="text-[28px] text-[#4A5568] my-0.5"><FaHandHoldingDollar /></div>
                      <p className="text-[14px] font-bold text-[#1A202C]">
                        {snapshot
                          ? `$${formatNumberShort(Number(snapshot.funding_received))}`
                          : <span className="text-xs text-gray-400">Loading...</span>}
                      </p>
                    </div>
                  </div>

                  {/* Percentage Funded */}
                  <p className="text-[10px] text-[#4A5568] text-right">
                    {snapshot
                      ? `${Math.round((snapshot.funding_received / snapshot.funding_required) * 100)}% funded`
                      : <span className="text-xs text-gray-400">Calculating...</span>}
                  </p>
                </div>

                {/* Reach Section */}
                <div className="space-y-3">
                  <p className="text-[12px] font-semibold text-[#2D3748]">
                    Reach ({snapshot ? new Date(snapshot.report_year, snapshot.report_month - 1).toLocaleString("default", { month: "short", year: "numeric" }) : "..."})
                  </p>
                  <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] text-center flex flex-col items-center space-y-1.5">
                    <p className="text-[10px] text-[#4A5568]">Individuals</p>
                    <div className="text-[28px] text-[#4A5568] my-0.5"><IoIosPeople /></div>
                    <p className="text-[14px] font-bold text-[#1A202C]">
                      {snapshot
                        ? `${formatNumberShort(Number(snapshot.population_reached))}`
                        : <span className="text-xs text-gray-400">Loading...</span>}</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="pt-1 border-t border-[#F3F4F6] mt-auto">
                  <p className="text-[10px] text-[#718096] text-right">
                    {snapshot
                      ? `${new Date(snapshot.report_year, snapshot.report_month - 1).toLocaleString("default", {
                        month: "short",
                        year: "numeric",
                      })} Data`
                      : "Loading..."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 2 && (
          <div>
            <section className="mt-0 mb-2">
              {/* Chart grid with matching spacing */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                <div className="shadow-lg py-6 px-0 rounded-lg bg-white">
                  <EvictionsAreaChart evictionsByMonth={evictionsByMonth} preventionsByMonth={preventionsByMonth} />
                </div>
                <div className="shadow-lg py-6 px-0 rounded-lg bg-white">
                  <EvictionsLineChart evictionsByMonth={evictionsByYear} preventionsByMonth={preventionsByYear} />
                </div>
              </div>

            </section>
          </div>
        )}

        {/* Add similar blocks for tabs 3, 4, etc. */}
        {tab === 3 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
              <div className="shadow-lg p-2 rounded-lg bg-white">
                <TopEvictionsBarUpdated title="Top 5 Regions By Evictions" data={topRegionsOverall} enableSeeMore={true} onToggleSeeMore={() => setExpandedChart("regions")} />
              </div>
              <div className="shadow-lg p-2 rounded-lg bg-white">
                <TopEvictionsBarUpdated title="Top 5 Districts By Evictions" data={topDistrictsOverall} enableSeeMore={true} onToggleSeeMore={() => setExpandedChart("districts")} />
              </div>
              <div className="shadow-lg p-2 rounded-lg bg-white">
                <TopEvictionsBarUpdated title="Top 5 Causes of Evictions" data={topReasonsOverall} />
              </div>
            </div>
            {expandedChart === "regions" && (
              <div className="shadow-lg p-4 rounded-lg bg-white mt-4 grid gap-4
                  grid-cols-1 md:grid-cols-10">

                {/* Chart section: 8/10 on medium+ screens, full width on small screens */}
                <div className="col-span-1 md:col-span-8">
                  <TopEvictionsBarUpdated title="Evictions Across All Regions" data={regionsOverall} />
                  <button
                    onClick={() => setExpandedChart(null)}
                    className="mt-2 text-[#2563EB] text-xs hover:underline"
                  >
                    Hide full chart
                  </button>
                </div>

                {/* Sidebar: 2/10 on medium+ screens, full width on small screens */}
                <div className="col-span-1 md:col-span-2">
                  <input
                    type="text"
                    placeholder="Search region..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border border-[#60A5FA] focus:border-[#2563EB] text-[#1E40AF] p-2 rounded w-full mb-2"
                  />
                  <ul className="border border-[#D1D5DB] rounded p-2 bg-white max-h-60 overflow-y-auto text-sm">
                    {regionsOverall
                      .filter(([name]) => name.toLowerCase().includes(searchTerm.toLowerCase()))
                      .map(([name, value]) => (
                        <li
                          key={name}
                          className="cursor-pointer hover:bg-[#F3F4F6] p-1"
                          onClick={() => setSelectedItem([name, value])}
                        >
                          {name} {/*({value.toLocaleString()})*/}
                        </li>
                      ))}
                  </ul>

                  {selectedItem && (
                    <div className="mt-2 text-sm text-[#374151]">
                      <strong>{selectedItem[0]}</strong>: {selectedItem[1].toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}

            {expandedChart === "districts" && (
              <div className="shadow-lg p-4 rounded-lg bg-white mt-4 grid gap-4 grid-cols-1 md:grid-cols-10">
                {/* 8/10 chart */}
                <div className="col-span-1 md:col-span-8">
                  <TopEvictionsBarUpdated title="Evictions Across All Districts" data={districtsOverall} />
                  <button
                    onClick={() => setExpandedChart(null)}
                    style={{ color: "#2563EB", fontSize: "0.75rem" }}
                    className="hover:underline mt-2"
                  >
                    Hide full chart
                  </button>
                </div>

                {/* 2/10 sidebar */}
                <div className="col-span-1 md:col-span-2">
                  <input
                    type="text"
                    placeholder="Search district..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border border-[#60A5FA] focus:border-[#2563EB] text-[#1E40AF] p-2 rounded w-full mb-2"
                  />
                  <ul className="border border-[#D1D5DB] rounded p-2 bg-white max-h-60 overflow-y-auto text-sm">
                    {districtsOverall
                      .filter(([name]) =>
                        name.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map(([name, value]) => (
                        <li
                          key={name}
                          className="cursor-pointer hover:bg-[#F3F4F6] p-1"
                          onClick={() => setSelectedItem([name, value])}
                        >
                          {name} {/*({value.toLocaleString()})*/}
                        </li>
                      ))}
                  </ul>

                  {selectedItem && (
                    <div className="mt-2 text-sm text-[#374151]">
                      <strong>{selectedItem[0]}</strong>: {selectedItem[1].toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        {tab === 4 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
              <div className="shadow-lg p-2 rounded-lg bg-white md:col-span-2">
                <StackedEvictionsHistogram evictionsByReasonsPerYear={allReasonsPerYearAgg} />
              </div>
              <div className="shadow-lg p-6 rounded-lg bg-white md:col-span-1">
                <EvictionReasonStatsCard evictionStats={topYearStats} />
              </div>
            </div>
          </div>
        )}
        {tab === 5 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="shadow-lg p-4 rounded-lg bg-white md:col-span-1">
                <EvictionReasonStatCard evictionStats={topReasonStats} />
              </div>
              <div className="shadow-lg p-4 rounded-lg bg-white md:col-span-2">
                <HistogramChart
                  evictionData={allRegionsPerReason}
                  xAxisLabel="Region"
                  title="Evictions by Reason Across Regions"
                />
              </div>
            </div>
          </div>
        )}
        {tab === 6 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {/* Heatmap takes full width on mobile, 2/3 on md+ */}
            <div className="sm:col-span-2 md:col-span-2 shadow-lg px-0 py-2 rounded-lg bg-white">
              <HeatmapChart data={processedDataAggregate} />
            </div>

            {/* Stats card takes full width on mobile, 1/3 on md+ */}
            <div className="sm:col-span-2 md:col-span-1 shadow-lg p-4 rounded-lg bg-white">
              <HeatMapStats
                monthlyAverage={monthlyStats.monthlyAverage}
                monthlyTrend={monthlyStats.monthlyTrend}
                peakMonths={monthlyStats.peakMonths}
                lowestMonths={monthlyStats.lowestMonths}
              />
            </div>
          </div>
        )}
        {tab === 7 && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
              <div className="shadow-lg py-6 px-2 rounded-lg bg-white">
                <ReasonsMonthLineChart data={processedDataAggregate.evictionsByReasonsPerMonth} title="Evictions by Reason (Individuals) by Month"
                  timePeriodLabel="Month" />
              </div>
              <div className="shadow-lg py-6 px-2 rounded-lg bg-white">
                <ReasonsYearLineChart data={processedDataAggregate.evictionsByReasonsPerYear} title="Evictions by Reason (Individuals) Over the Years"
                  timePeriodLabel="Year" />
              </div>
            </div>
          </div>
        )}

      </div>
      <div className="mt-10 px-4 sm:px-6">
        <p className="text-sm text-[#6B7280] mt-1">
          For more visit:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="bg-[#F9FAFB] p-3 rounded border border-[#E5E7EB] py-8 flex items-center gap-4">
            <div className="p-3">
              <svg
                width="40"
                height="40"
                viewBox="0 0 512 512"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="256" cy="256" r="256" fill="#f97316" />
                <path
                  fill="#ffffff"
                  d="M256 112c-8.8 0-16 7.2-16 16v16h-64c-8.8 0-16 7.2-16 16s7.2 16 16 16h8.5c-13.3 16.4-22.5 36.4-25.3 58.5C144.3 246.2 112 272 112 304c0 24.9 21.5 48 56 48s56-23.1 56-48c0-32-32.3-57.8-47.2-69.5 2.4-15.6 8.8-29.5 17.8-40.5H232v160h-32c-8.8 0-16 7.2-16 16s7.2 16 16 16h96c8.8 0 16-7.2 16-16s-7.2-16-16-16h-32V194h39.4c9 11 15.4 24.9 17.8 40.5C319.3 246.2 288 272 288 304c0 24.9 21.5 48 56 48s56-23.1 56-48c0-32-32.3-57.8-47.2-69.5-2.8-22.1-12-42.1-25.3-58.5h8.5c8.8 0 16-7.2 16-16s-7.2-16-16-16h-64v-16c0-8.8-7.2-16-16-16zm-88 192c0 6.2-7.6 16-24 16s-24-9.8-24-16c0-6.2 7.6-16 24-16s24 9.8 24 16zm200 0c0 6.2-7.6 16-24 16s-24-9.8-24-16c0-6.2 7.6-16 24-16s24 9.8 24 16z"
                />
              </svg>
            </div>
            <a href="https://www.nrc.no/what-we-do/activities-in-the-field/icla" className="text-left no-underline hover:text-[#374151]" target="_blank" rel="noopener noreferrer">
              <h1 className="text-[20px] font-bold text-[#1F2937]">Information Counselling and Legal Assistance (ICLA)</h1>
            </a>
          </div>

          <div className="bg-[#F9FAFB] p-3 rounded border border-[#E5E7EB] py-8 flex items-center gap-4">
            <div className="p-3">
              <img src={GCPIcon} alt="ICLA Icon" className="w-24 h-8" />
            </div>
            <a href="https://globalprotectioncluster.org/AoR/HLP" className="text-left no-underline hover:text-[#374151]" target="_blank" rel="noopener noreferrer">
              <h1 className="text-[20px] font-bold text-[#1F2937]">Global Housing, Land and Property (HLP) AoR Protection Cluster</h1>
            </a>
          </div>
        </div>
      </div>

      {/*  
      <div className="mt-10 px-4 sm:px-6">
        <p className="text-sm text-[#6B7280] mt-1">
          Master Housing, Land & Property essentials — start learning today!
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="bg-[#F9FAFB] p-3 rounded border border-[#E5E7EB] py-8 flex items-center gap-4">
            <div className="p-3">
              <img src={GCPIcon} alt="ICLA Icon" className="w-24 h-8" />
            </div>
            <a href="https://rootsdevelopmentinitaitive.h5p.com/content/1292659930993120197#h5pbookid=1292659930993120197&chapter=h5p-interactive-book-chapter-8e26b5ff-ab60-4815-b643-95b26947b9c8&section=0&focus=false" className="text-left no-underline hover:text-[#374151]" target="_blank" rel="noopener noreferrer">
              <h1 className="text-[20px] font-bold text-[#1F2937]">Housing, Land and Property Training Module</h1>
            </a>
          </div>
        </div>
      </div>
        */}
      <div className="mt-10 px-4 sm:px-6">
        <p className="text-sm text-[#6B7280] mt-1">
          For enquiries contact:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] flex flex-col items-center py-8">
            <BsPersonSquare className="text-8xl text-[#9CA3AF] mb-4" />
            <h3 className="text-lg font-semibold text-[#1F2937]">Osman Ali Ayeko</h3>
            <p className="text-sm text-[#6B7280] mt-1">HLP AoR Advisor</p>
            <div className="flex items-center text-sm text-[#4B5563] mt-1">
              <HiOutlineMail className="mr-1" />
              <span>
                <a href={`mailto:osman.ali@nrc.no`} className="text-[#2563EB] hover:text-[#1D4ED8] transition-colors duration-200">
                  osman.ali@nrc.no
                </a>
              </span>
            </div>
          </div>
          <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] flex flex-col items-center py-8">
            <BsPersonSquare className="text-8xl text-[#9CA3AF] mb-4" />
            <h3 className="text-lg font-semibold text-[#1F2937]">Abbas Ahmed</h3>
            <p className="text-sm text-[#6B7280] mt-1">ICLA Specialist</p>
            <div className="flex items-center text-sm text-[#4B5563] mt-1">
              <HiOutlineMail className="mr-1" />
              <span>
                <a href={`mailto:abbas.ahmed@nrc.no`} className="text-[#2563EB] hover:text-[#1D4ED8] transition-colors duration-200">
                  abbas.ahmed@nrc.no
                </a>
              </span>
            </div>
          </div>
          <div className="bg-[#F9FAFB] p-1.5 rounded border border-[#E5E7EB] flex flex-col items-center py-8">
            <BsPersonSquare className="text-8xl text-[#9CA3AF] mb-4" />
            <h3 className="text-lg font-semibold text-[#1F2937]">Zeinab Ahmed</h3>
            <p className="text-sm text-[#6B7280] mt-1 text-center">Eviction Task Lead & Director - Benadir Regional Administration</p>
            <div className="flex items-center text-sm text-[#4B5563] mt-1">
              <HiOutlineMail className="mr-1" />
              <span>
                <a href={`mailto:dsu@bra.gov.so`} className="text-[#2563EB] hover:text-[#1D4ED8] transition-colors duration-200">
                  dsu@bra.gov.so
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          textAlign: "right",
          fontSize: "0.9rem",
          color: "#555",
          margin: "1rem 2rem",
        }}
      >
        {stats ? (
          <>
            <strong>Unique visits today:</strong> {stats.new_today.toLocaleString()} &nbsp;|&nbsp;
            <strong>Total visits today:</strong> {stats.visits_today.toLocaleString()} &nbsp;|&nbsp;
            <strong>Total visits overall:</strong> {stats.total_unique.toLocaleString()}
          </>
        ) : (
          <span>Loading analytics...</span>
        )}
      </div>
    </div>
  );
};

export default Home;
